import {
  FocusLock,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import React, { memo, useCallback } from "react";
import colors from "app/colors";
import { GifIcon } from "modules/icons";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import GifPicker, { TenorImage } from "gif-picker-react";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { NEXT_PUBLIC_KLIPY_API_KEY } from "app/client";
import ClickOutsideBackdrop from "modules/_shared/click-outside-backdrop";

interface IProps {
  draftId: string;
}

const ComposeBottomToolbarGifButton = ({ draftId }: IProps) => {
  const { draft, isDraftLoaded, bottomButtonTypes, addGif } =
    useMessageThreadDraft({
      draftId,
    });

  const { onOpen, onClose, isOpen } = useDisclosure();

  const onGifClick = useCallback(
    (tenorImage: TenorImage) => {
      addGif(tenorImage);
      onClose();
    },
    [addGif, onClose],
  );

  if (!draft || !isDraftLoaded) return null;

  if (!bottomButtonTypes.includes("gif")) {
    return null;
  }

  return (
    <Popover
      placement="top"
      onOpen={onOpen}
      isOpen={isOpen}
      isLazy
      autoFocus={false}
      returnFocusOnClose={false}
    >
      <PopoverTrigger>
        <CircularIconButton
          aria-label="Add GIF"
          title="Add GIF"
          size="xs"
          variant="ghost"
          color={colors.gray40}
          icon={
            <GifIcon fill={colors.gray40} stroke={colors.gray40} boxSize={5} />
          }
          onClick={onOpen}
          tabIndex={-1}
        />
      </PopoverTrigger>
      <Portal>
        <ClickOutsideBackdrop isOpen={isOpen} onClose={onClose} />
        <PopoverContent borderRadius={10} w="500px">
          <PopoverArrow />
          <FocusLock>
            <GifPicker
              height={460}
              tenorApiKey={NEXT_PUBLIC_KLIPY_API_KEY}
              onGifClick={onGifClick}
              width="100%"
              autoFocusSearch
            />
          </FocusLock>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(ComposeBottomToolbarGifButton);
